/**
 * LeadSourceFormDialog - Modal de criacao/edicao de Origem custom.
 *
 * Origens de sistema (is_system=true) sao READ-ONLY: o dialog nao abre para elas.
 *
 * RELEASE: 2026-07-10 v1.0.0
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LEAD_SOURCE_ICON_LIBRARY, type LeadSourceEntry } from '@/lib/lead-sources';
import { useUpsertLeadSource } from '@/hooks/useLeadSources';
import { Megaphone } from 'lucide-react';

interface LeadSourceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preenche o form para edicao. Se ausente, entra em modo criacao. */
  editing?: LeadSourceEntry | null;
}

const ICON_NAMES = Object.keys(LEAD_SOURCE_ICON_LIBRARY).sort();

export function LeadSourceFormDialog({
  open,
  onOpenChange,
  editing,
}: LeadSourceFormDialogProps) {
  const [label, setLabel] = useState('');
  const [iconName, setIconName] = useState<string>('Megaphone');
  const [sortOrder, setSortOrder] = useState<number>(500);
  const [isActive, setIsActive] = useState(true);

  const upsert = useUpsertLeadSource();

  useEffect(() => {
    if (open) {
      setLabel(editing?.label ?? '');
      setIconName(editing?.iconName ?? 'Megaphone');
      setSortOrder(editing?.sort_order ?? 500);
      setIsActive(editing?.is_active ?? true);
    }
  }, [open, editing]);

  const handleSave = async () => {
    if (!label.trim()) return;
    await upsert.mutateAsync({
      id: editing?.id, // manter slug ao editar; criar novo se vazio
      label: label.trim(),
      icon: iconName,
      sort_order: sortOrder,
      is_active: isActive,
    });
    onOpenChange(false);
  };

  const PreviewIcon = LEAD_SOURCE_ICON_LIBRARY[iconName] ?? Megaphone;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editing ? 'Editar Origem' : 'Nova Origem'}
          </DialogTitle>
          <DialogDescription>
            {editing
              ? 'Ajuste o nome, ícone ou ordem de exibição.'
              : 'Cadastre uma nova origem para os negócios do CRM.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ls-label">Nome *</Label>
            <Input
              id="ls-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Feira Regional 2026"
              maxLength={60}
            />
            {!editing && (
              <p className="text-xs text-muted-foreground">
                O identificador único (slug) será gerado automaticamente a partir do nome.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="ls-icon">Ícone</Label>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-md border flex items-center justify-center bg-muted">
                <PreviewIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <Select value={iconName} onValueChange={setIconName}>
                <SelectTrigger id="ls-icon" className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {ICON_NAMES.map((name) => {
                    const Icon = LEAD_SOURCE_ICON_LIBRARY[name];
                    return (
                      <SelectItem key={name} value={name}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>{name}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ls-order">Ordem de exibição</Label>
            <Input
              id="ls-order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label className="text-sm">Ativa</Label>
              <p className="text-xs text-muted-foreground">
                Origens inativas não aparecem nos selects de novos negócios.
              </p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!label.trim() || upsert.isPending}>
            {upsert.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
